import { mutationOptions, type QueryClient } from "@tanstack/react-query";
import { invalidateResourceQueries } from "@softmax/web-features/data";
import { actualViewerQueryOptions } from "@softmax/web-features/session/records";
import { requestUnavailable } from "@softmax/observatory-client/transport";
import type { Schemas } from "@softmax/observatory-client/types";
import { accountProfileKey, type AccountUser } from "./account.queries";
import {
  removeAccountPhoto,
  saveAccountProfile,
  uploadAccountPhoto,
  type AccountProfileInput,
} from "./account.api";

export function accountProfileMutationOptions({
  queryClient,
  viewerId,
}: {
  queryClient: QueryClient;
  viewerId: string;
}) {
  const key = accountProfileKey(viewerId);
  const viewerKey = actualViewerQueryOptions().queryKey;
  return mutationOptions({
    mutationFn: (profile: AccountProfileInput) =>
      saveAccountProfile({ viewerId, profile }),
    onMutate: () => queryClient.cancelQueries({ queryKey: key, exact: true }),
    onSuccess: async ({ user }) => {
      await queryClient.cancelQueries({ queryKey: key, exact: true });

      if (requestUnavailable(queryClient.getQueryState(key)?.error ?? null))
        return;

      queryClient.setQueryData<AccountUser>(key, (current) =>
        current === undefined ? undefined : { ...current, ...user },
      );
      const identity = queryClient
        .getQueryCache()
        .find<Schemas["WhoAmIResponse"]>({ queryKey: viewerKey, exact: true });
      const principal = identity?.state.data;

      if (
        identity &&
        principal?.subject_type === "user" &&
        principal.subject_id === viewerId &&
        !requestUnavailable(identity.state.error)
      ) {
        await queryClient.cancelQueries(
          { predicate: (pending) => pending === identity },
          { revert: false },
        );
        const current = identity.state.data;

        if (
          queryClient
            .getQueryCache()
            .find({ queryKey: viewerKey, exact: true }) === identity &&
          current?.subject_type === "user" &&
          current.subject_id === viewerId &&
          !requestUnavailable(identity.state.error)
        ) {
          // A profile acknowledgment updates presentation without re-verifying identity or clearing a read failure.
          identity.setState({ data: { ...current, name: user.name } });
        }
      }

      void invalidateResourceQueries({
        client: queryClient,
        queryKeys: [key, ["admin-users-report"], ["posts"]],
      });
    },
    retry: false,
  });
}

export function accountAvatarMutationOptions({
  queryClient,
  viewerId,
}: {
  queryClient: QueryClient;
  viewerId: string;
}) {
  const key = accountProfileKey(viewerId);
  return mutationOptions({
    mutationFn: async (file: File | null) =>
      file
        ? { image: await uploadAccountPhoto({ viewerId, image: file }) }
        : removeAccountPhoto(viewerId),
    onMutate: () => queryClient.cancelQueries({ queryKey: key, exact: true }),
    onSuccess: async ({ image }) => {
      await queryClient.cancelQueries({ queryKey: key, exact: true });

      if (requestUnavailable(queryClient.getQueryState(key)?.error ?? null))
        return;

      queryClient.setQueryData<AccountUser>(key, (current) =>
        current === undefined ? undefined : { ...current, image },
      );
      void invalidateResourceQueries({
        client: queryClient,
        queryKeys: [key, ["admin-users-report"], ["posts"]],
      });
    },
    retry: false,
  });
}
